import {
    ArrowDownTrayIcon,
    BookmarkIcon,
    DocumentMagnifyingGlassIcon,
    EyeIcon,
    PaperAirplaneIcon,
} from '@heroicons/react/24/outline'
import {PrimaryButton} from '../../../../../../components/general/PrimaryButton.tsx'
import {SecondaryButton} from '../../../../../../components/general/SecondaryButton.tsx'
import {decimalToLocalizedString} from '../../../../../../lib/Money.ts'
import {InvoiceTotals, describeVatBreakdownEntry} from '../utils/invoiceLines.ts'

type InvoiceActionsBarProps = {
    totals: InvoiceTotals
    onSaveAndDownload: () => void
    onSendByEmail: () => void
    onSaveDraft: () => void
    onPreview: () => void
    isCreating: boolean
    isSendingEmail: boolean
    isSavingDraft: boolean
    isPreviewing: boolean
    createDisabled: boolean
    createDisabledReason?: string
    draftSavedLabel?: string
    onShowPreviewSheet?: () => void
}

export function InvoiceActionsBar({
    totals,
    onSaveAndDownload,
    onSendByEmail,
    onSaveDraft,
    onPreview,
    isCreating,
    isSendingEmail,
    isSavingDraft,
    isPreviewing,
    createDisabled,
    createDisabledReason,
    draftSavedLabel,
    onShowPreviewSheet,
}: InvoiceActionsBarProps) {
    const busy = isCreating || isSendingEmail || isSavingDraft || isPreviewing

    return (
        <div className="sticky bottom-0 bg-white pt-4 pb-4 border-t border-gray-200 z-10">
            <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3 mb-4">
                <div className="flex items-end gap-8">
                    <div>
                        <div className="text-xs text-gray-500">Totaal excl. btw</div>
                        <div className="text-base font-semibold">€ {decimalToLocalizedString(totals.totalExclVat)}</div>
                    </div>
                    {totals.vatBreakdown.map(entry => (
                        <div key={entry.vat}>
                            <div className="text-xs text-gray-500">{describeVatBreakdownEntry(entry)}</div>
                            <div className="text-base font-semibold">€ {decimalToLocalizedString(entry.amount)}</div>
                        </div>
                    ))}
                </div>
                <div className="text-right">
                    <div className="text-xs text-gray-500">Totaal te betalen</div>
                    <div className="text-2xl font-bold">€ {decimalToLocalizedString(totals.totalInclVat)}</div>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <PrimaryButton
                    title={isCreating ? 'Bezig met genereren...' : 'Opslaan en downloaden'}
                    icon={ArrowDownTrayIcon}
                    onClick={onSaveAndDownload}
                    disabled={busy || createDisabled}
                    disabledFeedback={createDisabledReason}
                />
                <PrimaryButton
                    title={isSendingEmail ? 'Bezig met versturen...' : 'Verstuur factuur'}
                    icon={PaperAirplaneIcon}
                    onClick={onSendByEmail}
                    disabled={busy || createDisabled}
                    disabledFeedback={createDisabledReason}
                />
                <SecondaryButton
                    title={isSavingDraft ? 'Opslaan...' : 'Opslaan als concept'}
                    icon={BookmarkIcon}
                    onClick={onSaveDraft}
                    disabled={busy || createDisabled}
                />
                {onShowPreviewSheet && (
                    <div className="xl:hidden">
                        <SecondaryButton
                            title="Voorbeeld bekijken"
                            icon={EyeIcon}
                            onClick={onShowPreviewSheet}
                        />
                    </div>
                )}
                <SecondaryButton
                    title={isPreviewing ? 'Voorbeeld maken...' : 'Voorbeeld als PDF'}
                    icon={DocumentMagnifyingGlassIcon}
                    onClick={onPreview}
                    disabled={busy || createDisabled}
                />
                {draftSavedLabel && (
                    <span className="text-xs text-gray-500">{draftSavedLabel}</span>
                )}
            </div>
        </div>
    )
}
